import { IActionTransition } from 'legacy/lut/classes/models/onboarding/ActionTransition'
import { CrudStoreItemOnboarding } from 'legacy/lut/classes/models/onboarding/CrudStoreItemOnboarding'
import { INotificationTemplate } from 'legacy/lut/classes/models/onboarding/INotificationTemplate'
import { RequestTypes } from 'legacy/lut/classes/models/onboarding/Onboarding'
import { IRequestTemplate } from 'legacy/lut/classes/models/onboarding/RequestTemplate'
import { getErrorResponse } from 'legacy/lut/functions'
import { uploadFiles } from 'legacy/lut/hooks/useFileUpload'
import { IFileSettings } from 'legacy/lut/library/dragNDrop'
import { appGetState } from 'legacy/lut/store'

import { downloadBlob } from 'clickNgo/helpers/common'
import { getTokens } from 'core/classes/helpers/StorageHelper'
import WebSocketStore from 'core/classes/services/WebSocketStore'
import { Crud } from 'core/types/Crud'
import { CrudStore } from 'core/types/crudStore'
import Logger from 'core/utils/Logger'
import { downloadFile } from 'shared/lib/http/downloadFile'
import { ENDPOINT_EXPORT_TEMPLATES } from 'src/config/url'

class OnboardingTemplateApi {
  public async getNotificationTemplatesAndPlaceholders(payload: {
    notificationTemplatesUUIDs: string[]
  }) {
    const wsClient = WebSocketStore.get('onboarding')
    let result

    try {
      result = await wsClient.call<{
        notificationTemplates: INotificationTemplate[]
        placeholders: Record<string, string>
      }>({
        service: 'notification-template',
        action: 'getTemplatesWithPlaceholders',
        payload,
        withoutTokenRefresh: true,
        isAuth: true,
      })
    } catch (e: any) {
      const errorMessage = 'Failed to getNotificationTemplatesAndPlaceholders'

      result = getErrorResponse(e.message || errorMessage, {
        notificationTemplates: null,
        placeholders: null,
      })
    }

    return result
  }

  public fetchTransitionById = async (id: number) => {
    let result = null
    if (id) {
      const source: Crud<IActionTransition, number> = CrudStore.get<IActionTransition, number>(
        CrudStoreItemOnboarding.ActionTransitionsTemplate
      )

      try {
        result = await source.getById({ id, force: true })
      } catch (e) {
        Logger.error(e)
      }
    }

    return result
  }

  public async exportRequestTemplate(requestTemplateUUIDs: string[]) {
    const [token] = getTokens(appGetState().app.ownerId)

    try {
      const { fileName, file } = await downloadFile({
        method: 'post',
        url: ENDPOINT_EXPORT_TEMPLATES,
        token,
        payload: { templateUUIDs: requestTemplateUUIDs },
      })

      if (!file) {
        Logger.error('Failed to export the request templates')

        return
      }

      downloadBlob(fileName ?? `${Date.now()}_onboarding_templates.json`, file)
    } catch (e) {
      Logger.error(e)
    }
  }

  public async cloneRequestTemplate(requestTemplateUUID: string) {
    const wsClient = WebSocketStore.get('onboarding')
    let result = null
    try {
      const response = await wsClient.call({
        service: 'request-template',
        action: 'clone',
        payload: { requestTemplateUUID },
        withoutTokenRefresh: true,
        isAuth: true,
      })

      if (response.status === 'success') {
        result = response.data as IRequestTemplate | null
      }
    } catch (e) {
      Logger.error(e)
    }

    return result
  }

  public async importRequestTemplate(filesInfo: IFileSettings[]): Promise<{
    structureImported: boolean
    attachmentsImported: boolean
    result: IRequestTemplate[]
  }> {
    const uploaded = await uploadFiles({
      filesInfo: filesInfo,
      endpoint: '/api/v0/import/templates',
    })
    const body = (await uploaded?.response.json()) as
      | {
          structureImported?: boolean
          attachmentsImported?: boolean
          result?: IRequestTemplate[]
        }
      | undefined

    return {
      structureImported: body?.structureImported ?? false,
      attachmentsImported: body?.attachmentsImported ?? false,
      result: body?.result ?? [],
    }
  }

  public async getRequestTemplateByRequestId(requestId: number): Promise<IRequestTemplate | null> {
    const wsClient = WebSocketStore.get('onboarding')
    let result = null
    try {
      const response = await wsClient.call<{ template: IRequestTemplate | null }>({
        service: 'request-template',
        action: 'template-by-request-id',
        payload: { requestId },
        withoutTokenRefresh: true,
        isAuth: true,
      })

      if (response.status === 'success') {
        result = response.data.template
      }
    } catch (e) {
      Logger.error(e)
    }

    return result
  }

  public async getAvailableCountriesByRequestType(requestType: RequestTypes): Promise<string[]> {
    const wsClient = WebSocketStore.get('onboarding')
    let result: string[] = []
    try {
      const response = await wsClient.call<string[]>({
        service: 'request-template',
        action: 'available-countries-by-request-type',
        payload: { requestType },
        withoutTokenRefresh: true,
        isAuth: true,
      })

      if (response.status === 'success') {
        result = response.data || []
      }
    } catch (e) {
      Logger.error(e)
    }

    return result
  }
}

export const onboardingTemplateApi = new OnboardingTemplateApi()
